<template>
  <div class="input-diagnosis" :class="{ types, dynamic }">

    <template v-if="reviewMode">
      <div class="review-item" v-for="(item, idx) in ivalue" :key="`diagnosis-${idx}`">

        <div class="item">
          <div class="item-group">
            <TextareaInput
              v-if="dataVersion === 1"
              :label="$t('comps.diagnosis.description')"
              :tooltip="$t('comps.diagnosis.tooltip')"
              :for-state="forState"
              :for-report="forReport"
              :value.sync="item.diagnosis"
              fill
            />
            <SelectEnhanced
              v-else-if="dataVersion !== 1 && !simplified"
              fill
              :label="$t('comps.diagnosis.chapter')"
              :tooltip="$t('comps.diagnosis.tooltip')"
              :for-state="forState"
              :for-report="forReport"
              :options="codes"
              :searchable="true"
              :value.sync="item.diagnosis"
            />
          </div>
          <div class="item-group">
            <TextInput
              fill
              :label="$t('comps.diagnosis.description')"
              :value.sync="item.dsm"
            />
          </div>
          <div class="item-group">
            <TextareaInput
              small
              :label="$t('comps.diagnosis.specialist')"
              tooltip="Beispiel: SPD / 2025"
              :for-report="forReport"
              :value.sync="item.specialist"
              fill
            />
            <Dropdown
              v-if="types"
              :label="$t('comps.diagnosis.diagnosesPrefix')"
              :for-state="forState"
              :options="computedTypes"
              :placeholder="item.placeholder || 'Keine Angabe'"
              static-placeholder
              @input="handleInput(idx, $event)"
              tertiary
              fill
            />
          </div>
        </div>

        <template v-if="ichanges[idx] !== null">
          <div class="item">
            <div class="item-group">
              <TextareaInput
                v-if="dataVersion === 1 && ichanges[idx].diagnosis"
                :label="$t('comps.diagnosis.description')"
                :tooltip="$t('comps.diagnosis.tooltip')"
                :for-state="forState"
                :for-report="forReport"
                :value.sync="item.diagnosis"
                fill
              />
              <SelectEnhanced
                v-else-if="ichanges[idx].diagnosis && !simplified"
                fill
                :label="`Neuer Wert: ${$t('comps.diagnosis.chapter')}`"
                :tooltip="$t('comps.diagnosis.tooltip')"
                :for-state="forState"
                :for-report="forReport"
                :options="codes"
                :searchable="true"
                :value.sync="ichanges[idx].diagnosis"
                disabled
              />
              <div v-else />
            </div>
            <div class="item-group">
              <TextInput
                fill
                :label="$t('comps.diagnosis.description')"
                :value.sync="ichanges[idx].dsm"
              />
            </div>
            <div class="item-group">
              <TextareaInput
                small
                v-if="ichanges[idx].specialist"
                :label="`Neuer Wert: ${$t('comps.diagnosis.specialist')}`"
                tooltip="Beispiel: SPD / 2025"
                :for-report="forReport"
                :value.sync="ichanges[idx].specialist"
                fill
                disabled
              />
              <div v-else/>
              <Dropdown
                v-if="types && ichanges[idx].type"
                :label="`Neuer Wert: ${$t('comps.diagnosis.diagnosesPrefix')}`"
                :for-state="forState"
                :options="computedTypes"
                :placeholder="ichanges[idx].placeholder || 'Keine Angabe'"
                static-placeholder
                @input="handleInput(idx, $event)"
                tertiary
                fill
                disabled
              />
              <div v-else-if="types"/>
            </div>
          </div>
          <template v-if="reviewMode">
            <button class="adopt" @click.stop.prevent="handleItemAdoption(idx, true)">{{ $t('review.diff.adopt') }}</button>
            <button class="adopt" @click.stop.prevent="handleItemAdoption(idx, false)">{{ $t('review.diff.ignore') }}</button>
          </template>
        </template>

      </div>
    </template>

    <template v-else>
      <div class="item" v-for="(item, idx) in ivalue" :key="`diagnosis-${idx}`">
        <div class="item-group">
          <TextareaInput
            v-if="dataVersion === 1"
            :label="$t('comps.diagnosis.description')"
            :tooltip="$t('comps.diagnosis.tooltip')"
            :for-state="forState"
            :for-report="forReport"
            :value.sync="item.diagnosis"
            fill
          />
          <SelectEnhanced
            v-else-if="dataVersion !== 1 && !simplified"
            fill
            :label="$t('comps.diagnosis.chapter')"
            :tooltip="$t('comps.diagnosis.tooltip')"
            :for-state="forState"
            :for-report="forReport"
            :options="codes"
            :searchable="true"
            :value.sync="item.diagnosis"
            @input="handleChapterSelect(idx, $event)"
          />
        </div>
        <div class="item-group">
          <TextInput
            fill
            :for-state="simplified"
            :disabled="disableInputs"
            :label="$t('comps.diagnosis.description')"
            :value.sync="item.dsm"
          />
        </div>
        <div class="item-group">
          <TextareaInput
            small
            :disabled="disableInputs"
            :label="$t('comps.diagnosis.specialist')"
            tooltip="Beispiel: SPD / 2025"
            :for-report="forReport"
            :value.sync="item.specialist"
            fill
          />
          <Dropdown
            v-if="types"
            :label="$t('comps.diagnosis.diagnosesPrefix')"
            :for-state="forState"
            :options="computedTypes"
            :disabled="disableInputs"
            :placeholder="item.placeholder || 'Keine Angabe'"
            static-placeholder
            @input="handleInput(idx, $event)"
            tertiary
            fill
          />
        </div>
        <div class="item-group right">
          <button class="action" @click="removeItem(idx)">Eintrag entfernen</button>
        </div>
      </div>
      <div v-if="dynamic && !disableInputs">
        <button class="action" @click="addItem">{{ computedAddLabel }}</button>
      </div>
    </template>
  </div>
</template>

<script>
  import SelectEnhanced from '@/components/ui/inputs/SelectEnhanced';
  import TextareaInput from '@/components/ui/inputs/TextareaInput';
  import TextInput from '@/components/ui/inputs/TextInput';
  import Dropdown from '@/components/ui/inputs/Dropdown';

  export default {
    name: 'DiagnosisInput',
    components: {
      SelectEnhanced,
      Dropdown,
      TextareaInput,
      TextInput,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      dataVersion: {
        type: Number,
        default: 2,
      },
      value: {
        type: Array,
        default: null,
      },
      types: {
        type: Boolean,
        default: false,
      },
      simplified: {
        type: Boolean,
        default: false,
      },
      codes: {
        type: Array,
        default: null,
      },
      diagnoses: {
        type: Array,
        default: () => [],
      },
      dynamic: {
        type: Boolean,
        default: false,
      },
      dynamicLabel: {
        type: Object,
        default: () => ({}),
      },
      forState: {
        type: Boolean,
        default: false,
      },
      forReport: {
        type: Boolean,
        default: false,
      },
      required: {
        type: Boolean,
        default: false,
      },
      reviewMode: {
        type: Boolean,
        default: false,
      },
      reviewId: {
        type: String,
        default: null,
      },
      reviewData: {
        type: Object,
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        ivalue: [{ description: null, specialist: null, type: null }],
        ichanges: [],
        preventValueChanges: false,
        disableInputs: false,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      computedTypes() {
        const diagnoses = this.diagnoses;
        const values = this.ivalue.map(v => v.type).filter(v => v !== null);
        return diagnoses.filter(d => !values.includes(d.value));
      },
      computedAddLabel() {
        if (this.ivalue.length === 0) {
          return this.dynamicLabel?.singular;
        }
        return this.dynamicLabel.plural;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      value: {
        handler(val) {
          this.preventValueChanges = true;
          if (val.length === 0) {
            this.disableInputs = false;
          }
          this.ivalue = val;
          this.$nextTick(() => {
            this.preventValueChanges = false;
          });
        },
      },
      ivalue: {
        handler(val) {
          if (!this.reviewMode && !this.preventValueChanges) {
            let out = val;
            if (this.simplified) {
              out = val.map(v => ({ dsm: v.dsm, specialist: v.specialist }));
            }
            this.$emit('update:value', out);
            this.$emit('input', out);
          }
        },
        deep: true,
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    created() {
      this.preventValueChanges = true;
      if (this.simplified && ((this.value || []).length === 0)) {
        this.ivalue = [];
      } else {
        this.ivalue = JSON.parse(JSON.stringify(this.value || [{ description: null, specialist: null, type: null }]));
      }
      if (this.reviewMode) {
        this.parseChanges();
      } else {
        this.$nextTick(() => {
          this.preventValueChanges = false;
        });
      }
    },
    // beforeMount() {},
    // render(h) { return h(); },
    mounted() {
      const value = this.ivalue || [];
      if (value.length === 1 && value.find(c => c.diagnosis === 'KA')) {
        this.disableInputs = true;
      }
    },
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      addItem() {
        const value = this.ivalue.slice();
        value.push({ description: null, specialist: null, type: null });
        this.ivalue = value;
      },
      removeItem(index) {
        const value = this.ivalue.slice();
        value.splice(index, 1);
        this.ivalue = value;
      },
      //----------------------------------
      // Review Methods
      //----------------------------------
      parseChanges() {
        const reviewId = this.reviewId;
        const reviewData = this.reviewData;
        if (reviewData && reviewId) {
          const ivalue = JSON.parse(JSON.stringify([...this.ivalue]));
          const review = JSON.parse(JSON.stringify(reviewData[reviewId]));
          const ichanges = [];
          ivalue.forEach((newval, idx) => {
            ichanges[idx] = null;

            const oldval = review[idx];
            if (oldval) {
              const changedType = newval.type !== oldval.type;
              const changedDiagnosis = newval.diagnosis !== oldval.diagnosis;
              const changedSpecialist = newval.specialist !== oldval.specialist;
              const changedDSM = newval.dms !== oldval.dsm;
              if (changedType || changedDiagnosis || changedSpecialist || changedDSM) {
                const obj = {};
                if (changedType) {
                  obj.type = oldval.type;
                  obj.placeholder = oldval.placeholder;
                }
                if (changedDiagnosis) {
                  obj.diagnosis = oldval.diagnosis;
                }
                if (changedDSM) {
                  obj.dsm = oldval.dsm;
                }
                if (changedSpecialist) {
                  obj.specialist = oldval.specialist;
                }
                ichanges[idx] = obj;
              }
            }
          });

          this.ichanges = ichanges;
        }
        this.preventValueChanges = false;
      },
      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleInput(index, value) {
        const values = JSON.parse(JSON.stringify(this.ivalue));
        const diagnose = this.diagnoses.find(v => v.value === value);

        values[index].placeholder = diagnose.label;
        values[index].type = value;
        this.$emit('update:value', values);
      },
      handleChapterSelect(idx, event) {
        this.disableInputs = false;
        if (idx === 0 && event === 'KA') {
          const values = JSON.parse(JSON.stringify(this.ivalue));
          values[0].placeholder = null;
          values[0].dsm = null;
          values[0].specialist = null;
          this.$emit('update:value', values);
          this.disableInputs = true;
        }
      },
      handleItemAdoption(idx, accept) {
        const reviewId = this.reviewId;
        const reviewData = this.reviewData;
        const ichanges = JSON.parse(JSON.stringify(this.ichanges));
        const ivalue = JSON.parse(JSON.stringify([...this.ivalue]));
        const review = JSON.parse(JSON.stringify(reviewData[reviewId]));

        if (accept) {
          ivalue[idx] = review[idx];
        }
        ichanges[idx] = null;
        this.ichanges = ichanges;
        this.ivalue = ivalue;

        const hasOpenChanges = ichanges.filter(o => o !== null).length > 0;
        if (!hasOpenChanges) {
          this.$emit('adopt', { accept: true, value: ivalue });
        }
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.input-diagnosis {
  border: 1px solid $color-black-solid-05;
  padding: 0;

  & > div.review-item {
    width: 100%;
    // border-top: 1px solid $color-black-solid-05;

    &:first-of-type {
      border-top: 0;
    }

    & > div.item {
      width: 100%;
      padding: 16px;

      &:last-of-type {
        border-bottom: 0;
      }

      & > .item-group {
        display: flex;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 16px;
        margin-bottom: 16px;

        & > div {
          width: 50%;
          &:only-child {
            width: 100%;
          }
        }

        &.right {
          justify-content: flex-end;
        }

        &:last-of-type {
          margin-bottom: 0;
        }
      }
    }
  }

  & > div.item {
    width: 100%;
    padding: 16px;
    border-bottom: 1px solid $color-black-solid-05;

    &:last-of-type {
      border-bottom: 0;
    }
    & > .item-group {
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
      gap: 16px;
      margin-bottom: 16px;

      & > div {
        width: 50%;
        &:only-child {
          width: 100%;
        }
      }

      &.right {
        justify-content: flex-end;
      }

      &:last-of-type {
        margin-bottom: 0;
      }
    }
  }

  &.types {
    div.item {
    }
  }

  button.action {
    background: transparent;
    outline: 0;
    border: 0;
    padding: 0;
    margin: 0;
    @include font-body-small;
    color: $color-black-alpha-60;
    text-decoration: underline;
    cursor: pointer;
  }

  button.adopt {
    display: inline-block;
    outline: 0;
    border: 0;
    cursor: pointer;
    background: transparent;
    text-decoration: underline;
    margin: 16px 0 0;
    @include font-body-small;
    color: $color-black-alpha-60;
  }
}
</style>
